/* ---------------------------------------------------------------------------
   The site's own works, drawn round the model — what the playback shows that
   the CSiBridge export has no part for.

     · an excavation (`Pit` in src/data/buildSequence.ts) — an open earth pit
       round the foundation at its support, battered at the sides, deepening as
       the digging goes on and shallowing as it is filled back, with a strip of
       ground round its rim so it reads as dug into something;
     · a cofferdam — sheet piles standing round a pit the record says had one,
       pulled once what stands on the foundation is up (`Pit.pulled`);
     · formwork — a see-through timber shell round a part standing in forms.

   EVERYTHING HERE IS SIZED OFF THE MODEL. A pit is the measured foundation plus
   working room; its depth is a cover over the footing, not a surveyed level —
   the record says how much was dug, never where the ground was.

   One group, added beside the structure rather than inside it, so nothing here is
   picked, measured or framed as a part. Rebuilt whole for each day shown: a site
   has a handful of pits and forms at once.
   --------------------------------------------------------------------------- */

import { partKind, type PartInfo, type Pit } from '../data/buildSequence.ts';

type Three = typeof import('three');
type Mesh = import('three').Mesh;
type Object3D = import('three').Object3D;
type Box3 = import('three').Box3;

/** Formwork standing on a part: timber, seen through. */
export const FORM = { colour: 0xc29457, edge: 0x6b4520, opacity: 0.45 };
/** How far the forms stand off the concrete they hold, in metres. */
const FORM_GAP = 0.2;

/** Working room round a foundation at the bottom of its pit, in metres. */
const WORKING_ROOM = 1;
/** Side slope of an open pit, horizontal per vertical. A cofferdam's sides are vertical. */
const BATTER = 0.75;
/** Width of the ground drawn round a pit's rim. */
const APRON = 2.5;
/** How far sheet piles are driven below the formation, and stand above the ground. */
const SHEET_TOE = 0.6;
const SHEET_HEAD = 0.8;

const LOOK = {
  light: { earth: 0x9c7a4f, earthOpacity: 0.55, ground: 0xb59a74, groundOpacity: 0.35, edge: 0x6e5434, sheet: 0x9aa6b4, sheetOpacity: 0.28, sheetEdge: 0x5f6b78 },
  dark: { earth: 0xa88658, earthOpacity: 0.5, ground: 0x8f7a5c, groundOpacity: 0.3, edge: 0xc4a77c, sheet: 0xa9b4c2, sheetOpacity: 0.22, sheetEdge: 0xc3ccd6 },
};

/**
 * A shell round a part standing in forms: the part's own triangles, scaled out
 * about its centre to stand FORM_GAP off every face, with its edges drawn so the
 * panels read as panels. The shell shares the part's geometry; the edges are its own.
 */
export function formShell(
  THREE: Three,
  part: Mesh,
  form: import('three').Material,
  edge: import('three').LineBasicMaterial,
): Object3D[] {
  const box = new THREE.Box3().setFromObject(part);
  if (box.isEmpty()) return [];
  const size = box.getSize(new THREE.Vector3());
  const centre = box.getCenter(new THREE.Vector3());
  const scale = new THREE.Vector3(
    size.x > 0 ? (size.x + 2 * FORM_GAP) / size.x : 1,
    size.y > 0 ? (size.y + 2 * FORM_GAP) / size.y : 1,
    size.z > 0 ? (size.z + 2 * FORM_GAP) / size.z : 1,
  );
  const matrix = new THREE.Matrix4()
    .makeTranslation(centre.x, centre.y, centre.z)
    .multiply(new THREE.Matrix4().makeScale(scale.x, scale.y, scale.z))
    .multiply(new THREE.Matrix4().makeTranslation(-centre.x, -centre.y, -centre.z))
    .multiply(part.matrixWorld);
  const shell = new THREE.Mesh(part.geometry, form);
  const edges = new THREE.LineSegments(new THREE.EdgesGeometry(part.geometry, 30), edge);
  for (const o of [shell, edges]) {
    o.matrixAutoUpdate = false;
    o.matrix.copy(matrix);
  }
  return [shell, edges];
}

/** Dispose what `formShell` and the pits made, sparing the parts' own geometry. */
function clear(group: import('three').Group, shared: ReadonlySet<unknown>): void {
  for (const child of [...group.children]) {
    const g = (child as Mesh).geometry;
    if (g && !shared.has(g)) g.dispose();
    group.remove(child);
  }
}

export interface SiteWorks {
  /** Draw the pits and forms of a day. `forms` are part indices; empty for none. */
  show(pits: readonly Pit[], dayIndex: number, forms: readonly number[]): { pits: number; forms: number };
  setTheme(theme: string): void;
  dispose(): void;
}

/** A pit's fixed shape: its formation, its ground level, and the rim at the ground. */
interface PitShape {
  formation: number;
  ground: number;
  /** Half-extents of the rim, and its centre, in plan. */
  cx: number;
  cz: number;
  hx: number;
  hz: number;
  batter: number;
}

export function mountSiteWorks(
  THREE: Three,
  beside: Object3D,
  meshes: readonly Mesh[],
  parts: readonly PartInfo[],
  theme: string,
): SiteWorks {
  const group = new THREE.Group();
  group.name = 'site-works';
  (beside.parent ?? beside).add(group);
  const shared = new Set<unknown>(meshes.map((m) => m.geometry));

  const base = { side: THREE.DoubleSide, flatShading: true, metalness: 0, roughness: 0.95 } as const;
  const earth = new THREE.MeshStandardMaterial({ ...base, transparent: true, depthWrite: false });
  const ground = new THREE.MeshStandardMaterial({ ...base, transparent: true, depthWrite: false });
  const earthEdge = new THREE.LineBasicMaterial();
  /* Seen through, as the forms are: the pit and the footing in it stay in view. */
  const sheet = new THREE.MeshStandardMaterial({ ...base, roughness: 0.6, transparent: true, depthWrite: false });
  const sheetEdge = new THREE.LineBasicMaterial();
  const form = new THREE.MeshStandardMaterial({ ...base, color: FORM.colour, transparent: true, opacity: FORM.opacity, depthWrite: false });
  const formEdge = new THREE.LineBasicMaterial({ color: FORM.edge });
  const paint = (next: string): void => {
    const look = next === 'dark' ? LOOK.dark : LOOK.light;
    earth.color.setHex(look.earth);
    earth.opacity = look.earthOpacity;
    ground.color.setHex(look.ground);
    ground.opacity = look.groundOpacity;
    earthEdge.color.setHex(look.edge);
    sheet.color.setHex(look.sheet);
    sheet.opacity = look.sheetOpacity;
    sheetEdge.color.setHex(look.sheetEdge);
  };
  paint(theme);

  /* The shapes, measured once per pit: the parts do not move under a playback. */
  const shapes = new WeakMap<Pit, PitShape | null>();
  const shapeOf = (pit: Pit): PitShape | null => {
    if (shapes.has(pit)) return shapes.get(pit)!;
    const box: Box3 = new THREE.Box3();
    for (const i of pit.around) if (meshes[i]) box.expandByObject(meshes[i]!);
    let shape: PitShape | null = null;
    if (!box.isEmpty()) {
      const kind = partKind(parts[pit.around[0]!]!.sourceType, parts[pit.around[0]!]!.label);
      /* Down to a footing's underside and a metre of cover over it; to a pile's
         head where the model has piles and no cap; under what stands there else. */
      let formation: number;
      let level: number;
      if (kind === 'pile') {
        formation = box.max.y - 1.5;
        level = box.max.y + 1;
      } else if (kind === 'footing' || kind === 'pilecap') {
        formation = box.min.y - 0.1;
        level = Math.max(box.max.y + 1, formation + 2.5);
      } else {
        formation = box.min.y - 0.3;
        level = formation + 2.5;
      }
      const batter = pit.cofferdam ? 0 : BATTER;
      const depth = level - formation;
      shape = {
        formation,
        ground: level,
        cx: (box.min.x + box.max.x) / 2,
        cz: (box.min.z + box.max.z) / 2,
        hx: (box.max.x - box.min.x) / 2 + WORKING_ROOM + batter * depth,
        hz: (box.max.z - box.min.z) / 2 + WORKING_ROOM + batter * depth,
        batter,
      };
    }
    shapes.set(pit, shape);
    return shape;
  };

  /** Triangles of the given quads, corners in order round each. */
  const quads = (list: readonly (readonly [number, number, number])[][]): import('three').BufferGeometry => {
    const out: number[] = [];
    for (const [a, b, c, d] of list) out.push(...a!, ...b!, ...c!, ...a!, ...c!, ...d!);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(out, 3));
    return g;
  };

  /** A rectangle in plan at height y, corners round it. */
  const rect = (s: PitShape, hx: number, hz: number, y: number): [number, number, number][] => [
    [s.cx - hx, y, s.cz - hz],
    [s.cx + hx, y, s.cz - hz],
    [s.cx + hx, y, s.cz + hz],
    [s.cx - hx, y, s.cz + hz],
  ];

  const drawPit = (s: PitShape, open: number, cofferdam: boolean): void => {
    const top = rect(s, s.hx, s.hz, s.ground);
    const inset = s.batter * open;
    const floor = rect(s, s.hx - inset, s.hz - inset, s.ground - open);
    const sides = [0, 1, 2, 3].map((k) => [top[k]!, top[(k + 1) % 4]!, floor[(k + 1) % 4]!, floor[k]!]);
    const pit = quads([...sides, floor]);
    group.add(new THREE.Mesh(pit, earth));
    group.add(new THREE.LineSegments(new THREE.EdgesGeometry(pit, 20), earthEdge));
    const rim = rect(s, s.hx + APRON, s.hz + APRON, s.ground);
    group.add(new THREE.Mesh(quads([0, 1, 2, 3].map((k) => [rim[k]!, rim[(k + 1) % 4]!, top[(k + 1) % 4]!, top[k]!])), ground));
    if (!cofferdam) return;
    /* The sheet piles: a thin wall on each side of the rim, toe below the formation. */
    const height = s.ground + SHEET_HEAD - (s.formation - SHEET_TOE);
    const mid = (s.ground + SHEET_HEAD + s.formation - SHEET_TOE) / 2;
    const t = 0.12;
    for (const [w, d, x, z] of [
      [2 * s.hx + t, t, s.cx, s.cz - s.hz - t / 2],
      [2 * s.hx + t, t, s.cx, s.cz + s.hz + t / 2],
      [t, 2 * s.hz + t, s.cx - s.hx - t / 2, s.cz],
      [t, 2 * s.hz + t, s.cx + s.hx + t / 2, s.cz],
    ] as const) {
      const box = new THREE.BoxGeometry(w, height, d);
      const wall = new THREE.Mesh(box, sheet);
      const edges = new THREE.LineSegments(new THREE.EdgesGeometry(box), sheetEdge);
      for (const o of [wall, edges]) {
        o.position.set(x, mid, z);
        group.add(o);
      }
    }
  };

  return {
    show(pits, day, forms) {
      clear(group, shared);
      let open = 0;
      for (const pit of pits) {
        const dug = pit.dug[day] ?? 0;
        const filled = pit.filled[day] ?? 0;
        const s = shapeOf(pit);
        if (!s || dug <= 0 || filled >= 1) continue;
        const depth = (s.ground - s.formation) * dug * (1 - filled);
        if (depth < 0.05) continue;
        drawPit(s, depth, pit.cofferdam && (pit.pulled === null || day < pit.pulled));
        open += 1;
      }
      let standing = 0;
      for (const i of forms) {
        const mesh = meshes[i];
        if (!mesh) continue;
        for (const o of formShell(THREE, mesh, form, formEdge)) group.add(o);
        standing += 1;
      }
      return { pits: open, forms: standing };
    },
    setTheme: paint,
    dispose() {
      clear(group, shared);
      group.removeFromParent();
      for (const m of [earth, ground, earthEdge, sheet, sheetEdge, form, formEdge]) m.dispose();
    },
  };
}
